// Wire protocol between browsers and the Choir Durable Object (JSON over WebSocket).

import { LANES, STEPS, VOICES, type VoiceName } from "./music";

export interface PlayerInfo {
  id: string;
  name: string;
  /** Hue in degrees, 0–359. */
  hue: number;
  voice: VoiceName;
  /** City-level location, slightly fuzzed. */
  lat: number;
  lon: number;
  city: string;
  country: string;
  continent: string;
  /** IATA code of the Cloudflare data center that terminated the player's connection. */
  colo: string;
  /** Ghosts are generative musicians the planet summons when it is lonely. */
  ghost?: boolean;
  joinedAt: number;
  /** Round-trip time to the Durable Object as measured by the player's browser. */
  rtt?: number;
}

export interface Cell {
  step: number;
  lane: number;
  owner: string;
  ownerName: string;
  hue: number;
  voice: VoiceName;
  lat: number;
  lon: number;
  colo: string;
  /** Bar index at which the cell was placed. */
  born: number;
  /** Lifetime in bars. */
  ttl: number;
}

export interface ServerHome {
  colo: string;
  city: string;
  lat: number;
  lon: number;
}

export interface Stats {
  online: number;
  notesEver: number;
  visitorsEver: number;
  countries: number;
  colos: number;
  /** Players online grouped by colo. */
  byColo: Record<string, number>;
}

export type ServerMsg =
  | {
      t: "welcome";
      room: string;
      you: PlayerInfo;
      players: PlayerInfo[];
      cells: Cell[];
      epoch: number;
      now: number;
      home: ServerHome;
      stats: Stats;
      narration?: Narration | null;
    }
  | { t: "pong"; c: number; s: number }
  | { t: "join"; player: PlayerInfo }
  | { t: "leave"; id: string }
  | { t: "cell"; step: number; lane: number; cell: Cell | null; by: string; at: number }
  | {
      t: "pluck";
      by: string;
      lane: number;
      vel: number;
      at: number;
      hue: number;
      voice: VoiceName;
      lat: number;
      lon: number;
      colo: string;
    }
  | { t: "cursor"; id: string; lat: number; lon: number }
  | { t: "clear"; by: string; at: number }
  | { t: "stats"; stats: Stats }
  | { t: "rtt"; id: string; ms: number }
  | ({ t: "narration" } & Narration)
  | { t: "error"; message: string };

export interface Narration {
  text: string;
  by: "ai" | "poet";
  at: number;
}

export type ClientMsg =
  | { t: "ping"; c: number }
  | { t: "toggle"; step: number; lane: number }
  | { t: "pluck"; lane: number; vel: number }
  | { t: "cursor"; lat: number; lon: number }
  | { t: "rename"; name: string }
  | { t: "rtt"; ms: number };

function isInt(v: unknown, lo: number, hi: number): v is number {
  return typeof v === "number" && Number.isInteger(v) && v >= lo && v <= hi;
}

function isNum(v: unknown, lo: number, hi: number): v is number {
  return typeof v === "number" && Number.isFinite(v) && v >= lo && v <= hi;
}

/** Validates an untrusted client message. Returns null for anything malformed. */
export function parseClientMsg(raw: unknown): ClientMsg | null {
  let m: unknown = raw;
  if (typeof raw === "string") {
    if (raw.length > 512) return null;
    try {
      m = JSON.parse(raw);
    } catch {
      return null;
    }
  }
  if (!m || typeof m !== "object") return null;
  const o = m as Record<string, unknown>;
  switch (o.t) {
    case "ping":
      return isNum(o.c, 0, Number.MAX_SAFE_INTEGER) ? { t: "ping", c: o.c } : null;
    case "toggle":
      return isInt(o.step, 0, STEPS - 1) && isInt(o.lane, 0, LANES - 1)
        ? { t: "toggle", step: o.step, lane: o.lane }
        : null;
    case "pluck":
      return isInt(o.lane, 0, LANES - 1) && isNum(o.vel, 0, 1)
        ? { t: "pluck", lane: o.lane, vel: o.vel }
        : null;
    case "cursor":
      return isNum(o.lat, -90, 90) && isNum(o.lon, -180, 180)
        ? { t: "cursor", lat: o.lat, lon: o.lon }
        : null;
    case "rtt":
      return isNum(o.ms, 0, 10_000) ? { t: "rtt", ms: Math.round(o.ms) } : null;
    case "rename": {
      if (typeof o.name !== "string") return null;
      const name = sanitizeName(o.name);
      return name ? { t: "rename", name } : null;
    }
    default:
      return null;
  }
}

export function sanitizeName(s: string): string {
  return s
    .normalize("NFKC")
    .replace(/[\u0000-\u001f\u007f-\u009f​-‏‪-‮⁦-⁩]/g, "")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 28);
}

export function isVoice(v: unknown): v is VoiceName {
  return typeof v === "string" && (VOICES as readonly string[]).includes(v);
}

export function cellKey(step: number, lane: number): number {
  return step * LANES + lane;
}

/** 0 → fully alive, 1 → about to vanish. */
export function cellAge(cell: Pick<Cell, "born" | "ttl">, bar: number): number {
  return Math.min(1, Math.max(0, (bar - cell.born) / cell.ttl));
}

export function cellAlive(cell: Pick<Cell, "born" | "ttl">, bar: number): boolean {
  return bar - cell.born < cell.ttl;
}
